import { useMemo } from 'react'
import type { Event, EventAttendanceStatus, GroupEvent } from '../types'

/**
 * Whether the viewer has a stake in an event, the bar a cancelled event must
 * clear to stay visible (see useVisibleEvents). Shared with global search.
 */
export function cancelledEventStake(
  eventStatuses: Map<string, EventAttendanceStatus>,
  allGroupEvents: GroupEvent[],
): (eventId: string) => boolean {
  const groupEventIds = new Set(allGroupEvents.map(ge => ge.eventId))
  return id => {
    const status = eventStatuses.get(id)
    return status === 'going' || status === 'interested' || groupEventIds.has(id)
  }
}

/**
 * Drops cancelled events the viewer has no stake in.
 *
 * A cancelled event still matters to whoever had it in their plans — they need
 * to be told it's off, and a silent disappearance just leaves a hole in their
 * schedule. To everyone else it's a dead row taking up space in a discovery
 * list, so it's filtered out.
 *
 * "In their plans" is deliberately narrow: the viewer's own going/interested
 * answer, or a group they're in that linked the event. A *friend's* interest is a
 * discovery signal, not a plan of the viewer's, and an explicit `not_going` is a
 * decision against — neither keeps a cancelled event on screen.
 *
 * Direct links are unaffected — EventPage reads the event on its own, so a
 * shared link still resolves and says the event was cancelled rather than 404ing.
 */
export function useVisibleEvents(
  events: Event[],
  eventStatuses: Map<string, EventAttendanceStatus>,
  allGroupEvents: GroupEvent[],
): Event[] {
  return useMemo(() => {
    // Identity-stable in the overwhelmingly common case — downstream memos and
    // useFrozenValue key off this array.
    if (!events.some(e => e.cancelled)) return events
    const hasStake = cancelledEventStake(eventStatuses, allGroupEvents)
    return events.filter(e => !e.cancelled || hasStake(e.id))
  }, [events, eventStatuses, allGroupEvents])
}
